import { useSnackbar } from 'notistack';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import UsersClient from 'src/clients/UsersClient';
import { Toast } from 'src/components/Toast';
import { PortalConfigContext } from 'src/context';
import { OnboardingStatus } from 'src/entities/OnboardingStatus';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RegularCardBox } from 'src/legacy/components/Cards/RegularCardBox';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { ApiTags, appAPI } from 'src/services/api/appApi';
import { usePatchDismissalStateMutation } from 'src/services/api/improvedOnboardingApi';
import { selectCurrentUser } from 'src/store/storeUtils';
import { AlertVariant } from 'src/store/ui/types';

import { TaskCodes } from './constants';
import { MapTaskCodeToTaskDetails, TaskDetailInfo } from './helper';
import { TaskDetails } from './TaskDetails';
import { TasksList } from './TasksList';
import { TasksProgress } from './TasksProgress';

interface OnboardingTasksSectionProps {
  onboardingStatus: OnboardingStatus;
}

const useStyles = makeStyles()((theme) => ({
  topContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  flexContainer: {
    display: 'flex',
    minHeight: 230,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  improvedOnboardingCard: {
    height: '100px',
  },
  sectionHeaderWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
  },
}));

export const OnboardingTasksSection: React.FC<OnboardingTasksSectionProps> = ({
  onboardingStatus,
}) => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const [patchDismissalState] = usePatchDismissalStateMutation();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const initialSelectedTask = onboardingStatus.additionalFields.tasks?.find(
    (t) => !t.done,
  );
  const [activeTaskCode, setActiveTaskCode] = React.useState<
    TaskCodes | undefined
  >(initialSelectedTask?.taskCode);

  const portalConfig = React.useContext(PortalConfigContext);
  const currentUser = useAppSelector(selectCurrentUser);
  const paymentInfo = useAppSelector((state) => state.payments.paymentInfo);

  const activeTaskDetails: TaskDetailInfo | null = activeTaskCode
    ? MapTaskCodeToTaskDetails(
        activeTaskCode,
        onboardingStatus.fields.isOnboardingTestClient,
        {
          portalName: portalConfig.name,
          currentUserEmail: currentUser?.fields.email ?? '',
        },
      )
    : null;

  // This is to handle the scenario if active task gets marked done and WSS event changes the status
  // of active task to done. In that scenario we want to select first not done task from the task list
  React.useEffect(() => {
    const selectedTask = onboardingStatus.additionalFields.tasks?.find(
      (t) => t.taskCode === activeTaskCode,
    );
    if (selectedTask?.done) {
      const firstNotDoneTask = onboardingStatus.additionalFields.tasks?.find(
        (t) => !t.done,
      );
      setActiveTaskCode(firstNotDoneTask?.taskCode);
    }
  }, [activeTaskCode, onboardingStatus]);

  React.useEffect(() => {
    const setupPaymentsTask = onboardingStatus.additionalFields.tasks?.find(
      (t) => t.taskCode === TaskCodes.TaskCodeSetupWithPayments,
    );
    const stripeAccountId = paymentInfo?.accountId;

    if (
      !setupPaymentsTask ||
      setupPaymentsTask.done ||
      !stripeAccountId ||
      stripeAccountId === 'restricted'
    ) {
      return;
    }

    const syncStripeOnboardingStatus = async () => {
      try {
        const result = await UsersClient.getAccount(stripeAccountId);
        if (!result?.data?.details_submitted) {
          return;
        }

        await UsersClient.completeStripeOnboarding(
          portalConfig.BaseAPIUrl,
          portalConfig.portalHeader,
        );

        dispatch(appAPI.util.invalidateTags([ApiTags.onboarding_task_status]));
      } catch (error) {
        console.error('Failed to sync stripe onboarding status', error);
      }
    };

    syncStripeOnboardingStatus();
  }, [
    onboardingStatus.additionalFields.tasks,
    paymentInfo?.accountId,
    portalConfig.portalHeader,
    portalConfig.BaseAPIUrl,
    dispatch,
  ]);

  const handleSelectTask = (code: TaskCodes) => {
    setActiveTaskCode(code);
  };

  const handleUndoDismiss = React.useCallback(async () => {
    await patchDismissalState({
      fields: { dismissed: false },
    });
  }, [patchDismissalState]);

  const successDismissSnackbar = React.useCallback(
    (snackbarKey: string) => (
      <div key={snackbarKey}>
        <Toast
          variant={AlertVariant.SUCCESS}
          onClose={() => closeSnackbar(snackbarKey)}
          description="Card dismissed"
          actionProps={{
            onClick: handleUndoDismiss,
            children: 'Undo',
            className: 'text-base',
          }}
        />
      </div>
    ),
    [closeSnackbar, handleUndoDismiss],
  );

  const handleDismissImprovedOnboarding = async () => {
    const onboardingStatusUpdated = await patchDismissalState({
      fields: { dismissed: true },
    }).unwrap();

    if (onboardingStatusUpdated.fields.dismissed) {
      // show snackbar to allow re-enabling
      enqueueSnackbar('Card has been dismissed', {
        content: (snackbarKey: string) => successDismissSnackbar(snackbarKey),
      });
    }
  };

  // if there are not task present then no need to show setup onboarding section
  if (
    !activeTaskDetails ||
    !activeTaskCode ||
    onboardingStatus.additionalFields?.tasks?.length === 0 ||
    onboardingStatus.fields.dismissed ||
    onboardingStatus.fields.creditsAwarded
  ) {
    return null;
  }

  const sectionItems = (
    <RegularCardBox onDismissClick={handleDismissImprovedOnboarding}>
      <div className={classes.topContainer}>
        <TasksProgress value={onboardingStatus.additionalFields.completion} />
        <div className={classes.flexContainer}>
          <TasksList
            onboardingStatus={onboardingStatus}
            activeTaskCode={activeTaskCode}
            onSelect={handleSelectTask}
          />
          <TaskDetails taskData={activeTaskDetails} />
        </div>
      </div>
    </RegularCardBox>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-black-heading pb-[16px] ">
        <BaseTypography fontType="16Medium">
          Finish onboarding and receive a $100 credit
        </BaseTypography>
        <BaseTypography
          data-testid="section-subtitle"
          className="text-secondary"
          fontType="13Medium"
        >
          {onboardingStatus.additionalFields.completion}% completed
        </BaseTypography>
      </div>

      <div className="flex flex-col gap-[20px]">{sectionItems}</div>
    </div>
  );
};
